/*
 * Copyright 2022 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useState } from 'react';

/**
 * Takes an initialization function and returns a memoized result of
 * that function. This value is not subject to cache purging
 * and will only call your initialization function once.
 *
 * ie
 * ```js
 * function SomeComponent() {
 *   const id = useInitializedValue(() => generateId());
 *   // ...
 * }
 * ```
 *
 * @param initializer Initialization function
 * @return result of initializer
 */
function useInitializedValue(initializer: unknown) {
  return useState(initializer)[0];
}

export default useInitializedValue;
